import type { SentimentCues, TranscriptSnippet } from "@expert/shared-types";

import { Card, Flex, Stack, Text } from "@mantine/core";
import { type JSX, useState } from "react";

import { useAnalytics } from "@/analytics";
import { ListeningAnimation } from "@/common-ui";
import { useSentimentListener } from "@/sentiment/hooks/useSentimentListener";
import { Sentiment } from "@/sentiment/Sentiment";
import { CollapseIcon } from "@/transcript/CollapseIcon";
import { useTranscriptSnippetListener } from "@/transcript/hooks/useTranscriptSnippetListener";
import { TranscriptBody } from "@/transcript/TranscriptBody";

export function Transcript({ taskStartTimeInMs }: { taskStartTimeInMs: number }): JSX.Element | undefined {
    const [isOpened, setIsOpened] = useState(true);
    const { dispatcher } = useAnalytics();
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [snippets, setSnippets] = useState<TranscriptSnippet[] | undefined>(undefined);
    const [sentiment, setSentiment] = useState<SentimentCues | undefined>(undefined);

    useSentimentListener(setSentiment);
    useTranscriptSnippetListener(setSnippets);

    return (
        <Card
            onTransitionEnd={() => setIsTransitioning(false)}
            style={{ transition: "all 200ms" }}
            w={isOpened ? "17rem" : "3.5rem"}
            miw={isOpened ? "12rem" : "2rem"}
            maw="25rem"
            p={0}
            radius="md"
            bg="dark.5"
            flex={isOpened ? "1 1 0" : undefined}
        >
            <Flex w="100%" h={isOpened ? "100%" : "60%"}>
                {!isTransitioning && (
                    <Stack w="100%" h="100%">
                        <Flex
                            direction={isOpened ? "row" : "column-reverse"}
                            align="center"
                            justify="space-between"
                            h={isOpened ? "auto" : "100%"}
                            w="100%"
                            p="md"
                            wrap="wrap"
                        >
                            <Flex align="left" direction={isOpened ? "row" : "column-reverse"}>
                                {!isOpened && snippets && <ListeningAnimation style={{ width: "1rem" }} />}
                                <Text
                                    mb={isOpened ? 0 : "1rem"}
                                    mt={isOpened ? 0 : "1rem"}
                                    style={isOpened ? { writingMode: "horizontal-tb" } : { writingMode: "sideways-lr" }}
                                    size="sm"
                                    fw="var(--mantine-font-weight-heavy)"
                                    c="dark.1"
                                >
                                    Live transcript
                                </Text>
                            </Flex>
                            <CollapseIcon
                                style={{ cursor: "pointer", transform: isOpened ? "" : "scaleX(-1)" }}
                                onClick={() => {
                                    void dispatcher.dispatchBusinessEvent(
                                        isOpened ? "TranscriptClosed" : "TranscriptOpened",
                                    );
                                    setIsTransitioning(true);
                                    setIsOpened(!isOpened);
                                }}
                                height="24px"
                                width="24px"
                            />
                        </Flex>
                        {isOpened && (
                            <TranscriptBody
                                hasSentiment={!!sentiment}
                                snippets={snippets}
                                taskStartTimeInMs={taskStartTimeInMs}
                            />
                        )}
                        {isOpened && sentiment && <Sentiment {...sentiment} />}
                    </Stack>
                )}
            </Flex>
        </Card>
    );
}
